import React, { useState, useEffect } from "react";
import {
    Alert,
    Box,
    Button,
    CardMedia,
    FormControl,
    FormHelperText,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    IconButton,
    Grid
} from "@mui/material/";
import { DateTimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import { helpers, format } from "../../../../../common/lib";
import { useSelector } from "react-redux";
import { strings } from "../../../../../common/i18n";
import styles from "../styles";
import UploadIcon from "@mui/icons-material/Upload";

const DealerDetails = ({ mode, initialData, onSave, onCancel, roles }) => {
    // State for dealer details
    const [dealer, setDealer] = useState({
        dealerContactEmail: "",
        dealerContactName: "",
        dealerContactRole: "",
        dealerContactComment: "",
        dealerContactSignature: "",
        dealerContactSignatureDate: dayjs(),
        auditorComments: "",
        auditorSignature: "",
        auditorSignatureDate: dayjs(),
        auditAssignment: ""
    });

    const { accounts, assignmentDetails } = useSelector((state) => state);

    const getAuditor = (auditorId) =>
        helpers.getListItemForId(accounts, auditorId)?.displayName;

    const [errors, setErrors] = useState({}); // Validation errors
    const [isFormValid, setIsFormValid] = useState(false); // Form validation status

    // Initialize state with initialData when in "edit" mode
    useEffect(() => {
        if (mode === "edit" && initialData) {
            setDealer(initialData);
            validateFields(initialData); // Validate initial data
        }
    }, [mode, initialData]);
    useEffect(() => {
        if (assignmentDetails.data?.length > 0 && !dealer.auditAssignment) {
            handleInputChange(
                "auditAssignment",
                assignmentDetails.data[0].id || assignmentDetails.data[0]
            );
        }
    }, [assignmentDetails.data]);

    // Handle input changes
    const handleInputChange = (field, value) => {
        const updatedDealer = {
            ...dealer,
            [field]: value
        };
        setDealer(updatedDealer);
        validateFields(updatedDealer); // Validate fields in real-time
    };

    // Handle image upload
    const handleImageUpload = (field, file) => {
        if (file) {
            const reader = new FileReader();
            reader.readAsDataURL(file); // Read file as Base64
            reader.onloadend = () => {
                const updatedDealer = {
                    ...dealer,
                    [field]: reader.result
                };
                setDealer(updatedDealer);
                validateFields(updatedDealer); // Validate fields after image upload
            };
        }
    };

    // Validate fields
    const validateFields = (dealer) => {
        const newErrors = {};

        // Validate Name
        if (!dealer.dealerContactName?.trim()) {
            newErrors.dealerContactName = "Name is required";
        }

        // Validate Email
        const emailRegex =
            /^(?!.*\.\.)(?!.*\.$)[a-zA-Z0-9._%+-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z]{2,})+$/;
        if (!dealer.dealerContactEmail?.trim()) {
            newErrors.dealerContactEmail = "Email is required";
        } else if (!emailRegex.test(dealer.dealerContactEmail)) {
            newErrors.dealerContactEmail = "Invalid email format";
        }

        // Validate Role
        if (!dealer.dealerContactRole) {
            newErrors.dealerContactRole = "Role is required";
        }

        // Validate Signature
        if (!dealer.dealerContactSignature) {
            newErrors.dealerContactSignature = "Signature is required";
        }

        // Validate  Auditor Signature
        if (!dealer.auditorSignature) {
            newErrors.auditorSignature = "Signature is required";
        }

        if (!dealer.dealerContactSignatureDate) {
            newErrors.dealerContactSignatureDate = "Date is required";
        }

        if (!dealer.auditorSignatureDate) {
            newErrors.auditorSignatureDate = "Date is required";
        }

        // Set errors and update validation status
        setErrors(newErrors);
        const isValid = Object.keys(newErrors).length === 0;
        setIsFormValid(isValid); // Update isFormValid state
        return isValid; // Return validation result
    };

    // Handle save
    const handleSave = () => {
        if (validateFields(dealer)) {
            onSave(dealer); // Pass dealer details to parent
        }
    };

    return (
        <Grid container spacing={2}>
            {/* Signature Upload */}
            <Grid item xs={12}>
                <Box sx={styles.signatureUploadContainer}>
                    <input
                        type="file"
                        accept="image/*"
                        onChange={(e) =>
                            handleImageUpload(
                                "dealerContactSignature",
                                e.target.files[0]
                            )
                        }
                        style={{ display: "none" }}
                        id="upload-signature"
                    />
                    <label htmlFor="upload-signature">
                        <CardMedia
                            component="img"
                            height="100"
                            image={
                                dealer.dealerContactSignature ||
                                "placeholder-image-url"
                            }
                            alt={strings("dealerSignature")}
                            sx={styles.cardMedia}
                        />
                    </label>
                    <label htmlFor="upload-signature">
                        <IconButton sx={styles.editIconButton} component="span">
                            <UploadIcon color="primary" />
                        </IconButton>
                    </label>

                    {errors?.dealerContactSignature?.length > 0 && (
                        <Alert severity="error">
                            {errors.dealerContactSignature}
                        </Alert>
                    )}
                </Box>
            </Grid>

            {/* Name Input */}
            <Grid item xs={6}>
                <TextField
                    label={strings("name")}
                    value={dealer.dealerContactName}
                    onChange={(e) =>
                        handleInputChange("dealerContactName", e.target.value)
                    }
                    fullWidth
                    size="small"
                    error={!!errors.dealerContactName}
                    helperText={errors.dealerContactName || " "}
                />
            </Grid>

            {/* Email Input */}
            <Grid item xs={6}>
                <TextField
                    label={strings("email")}
                    value={dealer.dealerContactEmail}
                    onChange={(e) =>
                        handleInputChange("dealerContactEmail", e.target.value)
                    }
                    fullWidth
                    size="small"
                    error={!!errors.dealerContactEmail}
                    helperText={errors.dealerContactEmail || " "}
                />
            </Grid>

            {/* Role Input */}
            <Grid item xs={6}>
                <FormControl
                    fullWidth
                    size="small"
                    error={!!errors.dealerContactRole}
                >
                    <InputLabel id="select-role-label">
                        {strings("role")}
                    </InputLabel>
                    <Select
                        value={dealer.dealerContactRole}
                        label={strings("role")}
                        onChange={(e) =>
                            handleInputChange(
                                "dealerContactRole",
                                e.target.value
                            )
                        }
                    >
                        {roles.map((role) => (
                            <MenuItem key={role.id} value={role.id}>
                                {role.name}
                            </MenuItem>
                        ))}
                    </Select>
                    <FormHelperText>
                        {errors.dealerContactRole || " "}
                    </FormHelperText>
                </FormControl>
            </Grid>

            <Grid item xs={6}>
                {renderDateTimePicker(
                    "Dealer Contact Date",
                    dealer.dealerContactSignatureDate,
                    (newValue) => {
                        handleInputChange(
                            "dealerContactSignatureDate",
                            newValue
                        );
                    },
                    errors.dealerContactSignatureDate
                )}
            </Grid>

            {/* Comment Input */}
            <Grid item xs={12}>
                <TextField
                    label={strings("dealerComments")}
                    value={dealer.dealerContactComment}
                    onChange={(e) =>
                        handleInputChange(
                            "dealerContactComment",
                            e.target.value
                        )
                    }
                    fullWidth
                    size="small"
                    helperText={" "}
                />
            </Grid>

            <Grid item xs={12}>
                <Box sx={styles.signatureUploadContainer}>
                    <input
                        type="file"
                        accept="image/*"
                        onChange={(e) =>
                            handleImageUpload(
                                "auditorSignature",
                                e.target.files[0]
                            )
                        }
                        style={{ display: "none" }}
                        id="auditor-upload-signature"
                    />
                    <label htmlFor="auditor-upload-signature">
                        <CardMedia
                            component="img"
                            height="100"
                            image={
                                dealer.auditorSignature ||
                                "placeholder-image-url"
                            }
                            alt={strings("auditorSignature")}
                            sx={styles.cardMedia}
                        />
                    </label>
                    <label htmlFor="auditor-upload-signature">
                        <IconButton sx={styles.editIconButton} component="span">
                            <UploadIcon color="primary" />
                        </IconButton>
                    </label>

                    {errors?.auditorSignature?.length > 0 && (
                        <Alert severity="error">
                            {errors.auditorSignature}
                        </Alert>
                    )}
                </Box>
            </Grid>

            {/* Assignment Selection */}
            <Grid item xs={6}>
                <FormControl fullWidth size="small">
                    <InputLabel id="assignment-select-label">
                        {strings("Auditor")}
                    </InputLabel>
                    <Select
                        labelId="assignment-select-label"
                        value={dealer.auditAssignment || ""}
                        label="Assignment"
                        onChange={(e) =>
                            handleInputChange("auditAssignment", e.target.value)
                        }
                    >
                        {assignmentDetails.data?.map((assignment) => (
                            <MenuItem key={assignment.id} value={assignment.id}>
                                {getAuditor(assignment.auditor)}
                            </MenuItem>
                        ))}
                    </Select>
                    <FormHelperText> </FormHelperText>
                </FormControl>
            </Grid>

            <Grid item xs={6}>
                {renderDateTimePicker(
                    "Auditor Date",
                    dealer.auditorSignatureDate,
                    (newValue) => {
                        handleInputChange("auditorSignatureDate", newValue);
                    },
                    errors.auditorSignatureDate
                )}
            </Grid>

            <Grid item xs={12}>
                <TextField
                    label={strings("auditorComments")}
                    value={dealer.auditorComments}
                    onChange={(e) =>
                        handleInputChange("auditorComments", e.target.value)
                    }
                    fullWidth
                    size="small"
                    helperText={" "}
                />
            </Grid>

            {/* Save and Cancel Buttons */}
            <Grid item xs={12}>
                <Box sx={styles.editActionButtons}>
                    <Button
                        variant="contained"
                        size="small"
                        onClick={handleSave}
                        disabled={!isFormValid} // Disable if form is invalid
                    >
                        {mode === "add" ? strings("add") : strings("save")}
                    </Button>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={onCancel} // Call onCancel from parent
                    >
                        {strings("cancel")}
                    </Button>
                </Box>
            </Grid>
        </Grid>
    );
};

const renderDateTimePicker = (label, value, onChange, error) => (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DateTimePicker
            label={label}
            value={value}
            onChange={onChange}
            disableFuture
            ampm={false}
            slotProps={{
                textField: {
                    size: "small",
                    fullWidth: true,
                    error: !!error,
                    helperText: error || " "
                }
            }}
        />
    </LocalizationProvider>
);

export default DealerDetails;
